import React, { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { ChevronDown, X } from "lucide-react";
import { Copy, TickCircle } from "iconsax-reactjs";
import clsx from "clsx";
import QRLogo from "asset/icons/logo.svg";
import { copyField } from "../../utilities/clipboard";
import { fetchCoinNetworks } from "api/fetchers";
import { useSelector } from "react-redux";

// network icons
import { BaseIcon, EthIcon, PolygonIcon } from "../../asset/network-bank-icons";
import TRC20 from "asset/icons/TRC20.svg";
import BEP20 from "asset/icons/BEP20.svg";
import CELO from "asset/icons/CELO.svg";
import SOL from "asset/icons/SOL.svg";

const ReceiveViaWalletModal = ({ isOpen, onClose, walletAddress }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [networks, setNetworks] = useState([]);
    const [selectedNetwork, setSelectedNetwork] = useState(null);
    const [copied, setCopied] = useState(false);
    const wrapperRef = useRef(null);

    const businessId = useSelector((s) => s?.business?._id);
    const currentCurrency = useSelector((s) => s?.currency);
    const coin = ["USDC", "USDT"].includes(String(currentCurrency).toUpperCase())
        ? currentCurrency
        : null;

    const pickIcon = (n) => {
        const key = String(n?.id || n?.name || n?.label || "").toLowerCase();

        if (n?.icon && React.isValidElement(n.icon)) {
            return React.cloneElement(n.icon, { className: "h-5 w-5" });
        }
        if (typeof n?.icon === "string" && n.icon) {
            return <img src={n.icon} alt="" className="h-5 w-5" />;
        }

        if (key.includes("erc") || key.includes("ethereum") || key.includes("eth"))
            return <EthIcon className="h-5 w-5" />;
        if (key.includes("trc") || key.includes("tron"))
            return <img src={TRC20} alt="TRC20" className="h-5 w-5" />;
        if (key.includes("bep") || key.includes("bsc") || key.includes("bnb"))
            return <img src={BEP20} alt="BEP20" className="h-5 w-5" />;
        if (key.includes("polygon") || key.includes("matic"))
            return <PolygonIcon className="h-5 w-5" />;
        if (key.includes("base")) return <BaseIcon className="h-5 w-5" />;
        if (key.includes("celo")) return <img src={CELO} alt="Celo" className="h-5 w-5" />;
        if (key.includes("sol") || key.includes("solana"))
            return <img src={SOL} alt="Solana" className="h-5 w-5" />;

        return <EthIcon className="h-5 w-5" />;
    };

    const selectedWalletAddress = selectedNetwork?.accountNumber || walletAddress || "";

    useEffect(() => {
        if (!isOpen || !businessId || !coin) {
            setNetworks([]);
            setSelectedNetwork(null);
            return;
        }

        let mounted = true;
        (async () => {
            const list = await fetchCoinNetworks({ currency: coin, businessId, normalize: true });
            const mapped = (list || []).map((n) => {
                const id = n.id || (n.name || n.label || "network").toString().toLowerCase();
                const name = n.name || n.label || "Network";
                const chain = n.label || n.name || "—";
                return {
                    id,
                    name,
                    chain,
                    accountNumber: n.accountNumber || "",
                    icon: n.icon || pickIcon(n),
                };
            });

            if (mounted) {
                setNetworks(mapped);
                setSelectedNetwork((current) => {
                    if (!mapped.length) return null;

                    return (
                        mapped.find((network) => network.id === current?.id) ||
                        mapped[0]
                    );
                });
            }
        })();

        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, businessId, coin]);

    useEffect(() => {
        const onDown = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    const handleCopy = async () => {
        if (!selectedWalletAddress) return;
        copyField(selectedWalletAddress, "Wallet address");
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[600] flex items-center justify-center bg-black/30">
            <div className="bg-white w-[90%] max-w-xl rounded-2xl p-6 relative">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-[600] text-[#161827]">
                        Receive {coin || "crypto"} via wallet address
                    </h2>
                    <button onClick={onClose}>
                        <X className="w-6 h-6 text-[#101828]" />
                    </button>
                </div>

                {/* Dropdown Label */}
                <label className="text-[#383B56] text-[14px] mb-2 block font-[450]">Network</label>

                {/* Select Dropdown */}
                <div
                    ref={wrapperRef}
                    className="relative bg-[#F4F5FF] rounded-xl px-4 py-3 cursor-pointer select-none"
                    onClick={() => setDropdownOpen((v) => !v)}
                >
                    <div className="flex justify-between items-center">
                        {selectedNetwork ? (
                            <div className="flex items-center gap-2">
                                <span className="h-8 w-8 rounded-full bg-[#F2F3FC] grid place-items-center">
                                    {selectedNetwork.icon}
                                </span>
                                <span className="text-sm text-[#101828]">
                                    {selectedNetwork.name} ({selectedNetwork.chain})
                                </span>
                            </div>
                        ) : (
                            <span className="text-sm text-[#9396B4]">Select network</span>
                        )}
                        <ChevronDown className="text-[#9396B4]" size={18} />
                    </div>

                    {/* Dropdown Options */}
                    {dropdownOpen && (
                        <div className="absolute z-10 mt-2 left-0 w-full bg-white border border-[#EEF0F6] rounded-xl shadow-md max-h-60 overflow-y-auto">
                            <p className="text-[14px] text-[#6F739B] font-[350] px-4 pt-3 pb-1">
                                You can receive {coin} via any of these supported networks
                            </p>

                            {networks.map((network) => (
                                <div
                                    key={network.id}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedNetwork(network);
                                        setDropdownOpen(false);
                                    }}
                                    className={clsx(
                                        "flex items-center justify-between p-3 m-2 rounded-xl cursor-pointer hover:bg-[#F9FAFB]",
                                        selectedNetwork?.id === network.id
                                            ? "bg-[#F4F5FF] border-1 border-solid !border-[#6F00FF]"
                                            : "border border-transparent border-t border-[#F1F1F1]"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="h-9 w-9 rounded-full bg-[#F2F3FC] grid place-items-center">
                                            {network.icon}
                                        </span>

                                        <div>
                                            <p className="text-sm font-medium text-[#101828]">
                                                {network.name}
                                            </p>
                                            <p className="text-xs text-[#9396B4]">
                                                {network.chain}
                                            </p>
                                        </div>
                                    </div>

                                    {selectedNetwork?.id === network.id && (
                                        <TickCircle
                                            className="text-[#6F00FF]"
                                            variant="Bold"
                                            size={20}
                                        />
                                    )}
                                </div>
                            ))}

                            {!networks.length && (
                                <div className="px-4 py-3 text-sm text-[#6F739B]">
                                    No networks available.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* QR & Address */}
                {selectedNetwork && (
                    <>
                        <div className="flex items-center gap-2">
                            <div className="mt-6 flex justify-center">
                                <div className="relative bg-white p-2 rounded-xl">
                                    <QRCode
                                        value={selectedWalletAddress}
                                        size={160}
                                        fgColor="#6F00FF"
                                        bgColor="#ffffff"
                                        style={{ height: "160px", width: "160px" }}
                                    />
                                    <img
                                        src={QRLogo}
                                        alt="Logo"
                                        className="absolute top-1/2 left-1/2 w-8 h-8 transform -translate-x-1/2 -translate-y-1/2 rounded-md"
                                    />
                                </div>
                            </div>

                            <div className="mt-4">
                                <p className="text-[14px] text-[#6F739B] font-[350] mb-1">
                                    Wallet address
                                </p>
                                <p className="text-[#161827] font-[450] text-[16px] mb-2 break-all">
                                    {selectedWalletAddress || "—"}
                                </p>
                                <button
                                    onClick={handleCopy}
                                    className="text-[#6F00FF] mt-1 flex gap-1 text-sm font-medium"
                                    disabled={!selectedWalletAddress}
                                >
                                    <Copy size="22" />
                                    {copied ? "Copied!" : "Copy"}
                                </button>
                            </div>
                        </div>

                        <p className="text-[14px] text-[#383B56] font-[350] mt-4">
                            ⚠️ Only send {coin} via the selected network to this address. Sending
                            through other networks may result in loss.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default ReceiveViaWalletModal;
